/**
 * Logix DUE Calculation — JS port of DueCalculationService (app/services/due_calculation_service.rb).
 * Pure function: no side effects, no DB access.
 * Server re-calculates authoritatively on reconnect.
 */

/**
 * Calculate DUE status for a list of items.
 * @param {Array<Object>} items - items with { id, name, frequency, interval_value, interval_unit }
 * @param {Object} lastCompletions - { [item_id]: ISO 8601 timestamp }
 * @param {Date|string} referenceTime - current time for calculation (default: now)
 * @returns {Array<Object>} - { item_id, item_name, frequency, due, last_completed_at }
 */
export function calculateDue(items, lastCompletions = {}, referenceTime = new Date()) {
  const ref = typeof referenceTime === "string" ? new Date(referenceTime) : referenceTime

  return items.map((item) => {
    const lastCompleted = lastCompletions[item.id]
      ? new Date(lastCompletions[item.id])
      : null

    return {
      item_id: item.id,
      item_name: item.name,
      frequency: item.frequency,
      due: isDue(item, lastCompleted, ref),
      last_completed_at: lastCompletions[item.id] || null
    }
  })
}

function isDue(item, lastCompleted, referenceTime) {
  // First-time items are always DUE
  if (!lastCompleted) return true

  switch (item.frequency) {
    case "shift":
      return true // Shift items are always DUE (per-shift)
    case "daily":
      return !completedToday(lastCompleted, referenceTime)
    case "weekly":
      return !completedThisWeek(lastCompleted, referenceTime)
    case "custom":
      return customIntervalElapsed(item, lastCompleted, referenceTime)
    default:
      return true
  }
}

function completedToday(lastCompleted, referenceTime) {
  return toDateString(lastCompleted) === toDateString(referenceTime)
}

function completedThisWeek(lastCompleted, referenceTime) {
  // Week starts Monday (matching Ruby's beginning_of_week(:monday))
  const monday = getMonday(referenceTime)
  return lastCompleted >= monday
}

function customIntervalElapsed(item, lastCompleted, referenceTime) {
  if (!item.interval_value || !item.interval_unit) return true

  let elapsedMs
  switch (item.interval_unit) {
    case "hours":
      elapsedMs = item.interval_value * 60 * 60 * 1000
      break
    case "days":
      elapsedMs = item.interval_value * 24 * 60 * 60 * 1000
      break
    default:
      return true
  }

  return (lastCompleted.getTime() + elapsedMs) <= referenceTime.getTime()
}

// Helper: YYYY-MM-DD string for date comparison (IST, not UTC)
function toDateString(date) {
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
}

// Helper: get Monday of the current week
function getMonday(date) {
  const d = new Date(date)
  const day = d.getDay()
  // Sunday = 0, Monday = 1, ..., Saturday = 6
  const diff = day === 0 ? 6 : day - 1
  d.setDate(d.getDate() - diff)
  d.setHours(0, 0, 0, 0)
  return d
}
